import { App_Url_New } from "@/shared/common/static";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { whiteLabelResources } from "./resources";
import { WhiteLabelContactButton } from "./types";

const contactButtons: WhiteLabelContactButton[] = [
  {
    title: "Layla bot",
    subtitle: "Message us",
  },
  {
    title: "WhatsApp",
    subtitle: "Chat with us",
    href: "https://wa.me/+919999999999",
  },
];

export const whiteLabelContent = {
  app: {
    title: "NexInsure - OC",
    faviconPath: whiteLabelResources.App_Url.public.Favicon,
  },
  brand: {
    logo: {
      src: whiteLabelResources.App_Url.public.BrandLogo,
      alt: "NexInsure logo",
      width: 400,
      height: 100,
    },
    height: "",
    fallbackText: "",
  },
  company: {
    poweredBy: "NexInsure",
    legalEntity: "NexInsure Insurance",
  },

  contact: {
    phoneLabel: "+91 9999999999",
    phoneValue: "+91 9999999999",
    whatsappNumber: "+919999999999",
    generalEmail: "info@nexsysit.co.in",
    customerServiceEmail: "hello@nexsysit.co.in",
    chatbotAlertMessage: "Chatbot feature coming soon!",
    chatbotName: "Layla",
  },
  homepage: {
    hero: {
      eyebrow: "Protect your car, home, health, and every journey with confidence.",
      titleLines: ["Protect. Insure.", "Relax."],
      ctaLabel: "GET QUOTE",
      featuredProductPath: App_Url_New.D2CPageUrl.insurance,
    },

    stats: {
      dealsTitle: "Complete Protection",
      dealsSubtitle: "Motor • Health • Travel • Home",
      policyholdersCount: "50K+",
      policyholdersLabel: "Policies protected",
    },

    whyChooseUs: {
      heading: "Why choose NexInsure?",
      imageAlt: "Why NexInsure",
      features: [
        {
          title: "One Trusted Insurer",
          description: "Reliable protection for Motor, Health, Travel, and Home insurance.",
        },
        {
          title: "Instant Digital Quotes",
          description: "Get quick quotes and purchase your policy in minutes.",
        },
        {
          title: "Comprehensive Protection",
          description: "Secure every aspect of your lifestyle with one insurance partner.",
        },
        {
          title: "Fast Claims Support",
          description: "Dedicated claims assistance whenever you need it.",
        },
      ],
    },

    offers: {
      heading: "Exclusive NexInsure offers",
      items: [
        {
          title: "Motor Insurance Benefits",
          description: "Comprehensive protection for your car with flexible coverage options.",
          terms: "* T&C apply",
        },
        {
          title: "Health & Home Protection",
          description: "Safeguard your family’s health and your home with trusted coverage.",
          terms: "* T&C apply",
        },
        {
          title: "Travel with Confidence",
          description: "Enjoy worldwide travel protection with reliable emergency assistance.",
          terms: "* T&C apply",
        },
      ],
    },

    products: {
      eyebrowPrefix: "Protect. Insure.",
      eyebrowHighlight: "Relax.",
      heading: "Non-life insurance for every need",
      ctaLabel: "GET QUOTE",
    },

    mascot: {
      heading: "Meet Layla",
      description:
        "Your trusted guide for simple and hassle-free Motor, Health, Travel, and Home insurance.",
      imageAlt: "NexInsure Layla",
      pillars: [
        {
          title: "Insure",
          description: "Find the right cover for your car, home, health, or travel.",
        },
        {
          title: "Claim",
          description: "Get fast and reliable support throughout your claims journey.",
        },
        {
          title: "Manage",
          description: "Keep all your insurance policies secure and accessible in one place.",
        },
      ],
    },

    contactSection: {
      heading: "Got question?",
      buttons: contactButtons,
    },
    lifeContent: {
      hero: {
        eyebrow: "Protect your family and grow your savings with the right life plan.",
        titleLines: ["Secure. Grow.", "Live."],
        featuredProductPath: App_Url_New.D2CPageUrl.insurance.lifeGetQuote,
      },

      whyChooseUs: {
        heading: "Why choose NexInsure for Life Insurance?",
        features: [
          {
            title: "Trusted NexInsure Protection",
            description: "Reliable life coverage from NexInsure with long-term financial security.",
          },
          {
            title: "Instant Digital Journey",
            description: "Buy your life policy quickly with a seamless online experience.",
          },
          {
            title: "Family Financial Security",
            description: `Protect your loved ones with comprehensive life insurance benefits.`,
          },
          {
            title: "Dedicated Claims Support",
            description: "Expert assistance throughout your policy and claims journey.",
          },
        ],
      },

      offers: {
        heading: "Tap into limited-time life insurance offers",
      },

      products: {
        eyebrowPrefix: "Secure. Grow.",
        eyebrowHighlight: "Live.",
        heading: "Life and investment products in your hands",
      },

      mascot: {
        description: "Your trusted guide for simple and hassle-free life insurance solutions.",
      },
    },
  },
  healthInsurancePage: {
    hero: {
      eyebrowPrefix: "Click. Pick.",
      eyebrowHighlight: "Insure.",
      title: "Health Insurance",
      subtitle: "Reliable health insurance made easy",
      ctaLabel: "GET QUOTE",
      imageAlt: "Health insurance NexInsure mascot",
    },
    whyBuy: {
      heading: `Why buy health insurance in the ${LOCALISATION_CONFIG.country}?`,
      reasons: [
        {
          title: "It's mandatory",
          description:
            "Employers cover employees, but you'll need separate plans for your spouse, kids, or household help.",
        },
        {
          title: "Shield yourself from costly medical bills",
          description: `${LOCALISATION_CONFIG.country} healthcare is among the world's best, but it can be expensive. Insurance keeps you protected.`,
        },
        {
          title: "Get access to top hospitals & specialists",
          description:
            "Enjoy coverage at leading clinics for check-ups, screenings, and emergency care.",
        },
        {
          title: "Relax knowing your loved ones are covered",
          description: `Most ${LOCALISATION_CONFIG.country} residents are expats. Insurance gives you peace of mind, even when you're far from home.`,
        },
      ],
    },
    howToChoose: {
      heading: "How to choose the right health insurance plan",
      description: `Choosing the right health insurance plan in ${LOCALISATION_CONFIG.country} requires careful consideration of your needs, budget, and legal requirements. Here's a step-by-step guide to help you make the best choice:`,
      items: [
        {
          id: "rules",
          title: `Understand the ${LOCALISATION_CONFIG.country} Health Insurance rules`,
          content: [
            {
              type: "paragraph",
              label: "Mandatory Coverage:",
              text: `Health insurance is compulsory in ${LOCALISATION_CONFIG.country}. Employers must provide coverage for employees, but dependents (spouse, children, domestic workers) may need separate plans.`,
            },
            {
              type: "paragraph",
              label: "DHA & HAAD Compliance:",
              text: `Ensure the policy meets ${LOCALISATION_CONFIG.country} Health Authority regulations.`,
            },
          ],
        },
        {
          id: "needs",
          title: "Assess your healthcare needs",
          content: [
            {
              type: "list",
              items: [
                {
                  label: "Individual or Family:",
                  text: "If you have dependents, choose a plan that covers maternity, pediatric care, and vaccinations.",
                },
                {
                  label: "Pre-existing Conditions:",
                  text: "If you have chronic illnesses (diabetes, hypertension), ensure the policy covers pre-existing conditions.",
                },
                {
                  label: "Regular Check-ups & Preventive Care:",
                  text: "Some plans offer annual screenings, wellness programs, and vaccinations.",
                },
              ],
            },
          ],
        },
        {
          id: "coverage",
          title: "Compare coverage & benefits",
          content: [
            {
              type: "list",
              intro: "Look for a policy that includes:",
              items: [
                {
                  text: "Inpatient & Outpatient Care – Hospitalisation, doctor visits, diagnostic tests.",
                },
                {
                  text: "Maternity & Newborn Care – Coverage for pregnancy, delivery, and baby care.",
                },
                {
                  text: "Emergency & Ambulance Services – Road ambulance and emergency treatment.",
                },
                { text: "Pharmacy & Medication Cover – Ensure prescribed medicines are included." },
                {
                  text: "Specialist Consultations – Coverage for consultations with specialists and referrals.",
                },
                {
                  text: "Dental & Optical Coverage (if needed) – Some policies offer optional dental and eye care.",
                },
                {
                  text: "Alternative Medicine – Covers the cost of medication for other treatments such as Homeopathy or Ayurveda.",
                },
              ],
            },
          ],
        },
        {
          id: "network",
          title: "Check the insurance network",
          content: [
            {
              type: "paragraph",
              label: "Hospitals & Clinics:",
              text: "Ensure the policy includes hospitals and clinics near your home or workplace.",
            },
            {
              type: "paragraph",
              label: "Direct Billing vs. Reimbursement:",
              text: "Some plans offer cashless treatment, while others require pay & claim reimbursement.",
            },
          ],
        },
        {
          id: "premiums",
          title: "Compare premiums & co-payments",
          content: [
            {
              type: "paragraph",
              label: "Premium:",
              text: "The amount you pay for the policy (monthly/annually).",
            },
            {
              type: "paragraph",
              label: "Co-payment:",
              text: "A percentage (e.g., 20%) that you pay for medical services.",
            },
          ],
        },
      ],
    },
    cta: {
      prompt: "Ready to get health insurance?",
      ctaLabel: "GET QUOTE",
    },
    benefits: {
      heading: "Your health insurance benefits",
      cards: [
        {
          imageAlt: "Doctor consultation with InsureHub mascot",
          items: [
            `Worldwide cover up to ${LOCALISATION_CONFIG.currencySymbol} 1 million`,
            "Choose your co-pay (pick the option that suits your budget)",
            "Get a second medical opinion (so you feel confident)",
            "Save 10% on a family plan (if you're covering four)",
          ],
        },
        {
          imageAlt: "Medical equipment with doctor",
          items: [
            "Maternity & newborn care (for expecting parents)",
            `Dental cover up to ${LOCALISATION_CONFIG.currencySymbol} 3000 a year`,
            `Optical cover up to ${LOCALISATION_CONFIG.currencySymbol} 1500 a year`,
            "Alternative therapy cover (homeopathy, Ayurveda, and more)",
          ],
        },
      ],
    },
    whyChoose: {
      heading: "Why choose InsureHub?",
      features: [
        {
          icon: "zap",
          title: "Exciting Propositions",
          description:
            "Enjoy special offers for everyone, get global emergency medical assistance anytime, anywhere, and access our preferred healthcare network, MedNet, NAS and Nextcare.",
        },
        {
          icon: "shield",
          title: "Backed by NexAccel",
          description:
            "You're in good hands. InsureHub is powered by NexAccel digital insurance technology.",
        },
        {
          icon: "headset",
          title: "Professional advisors",
          description:
            "Based on your unique needs, our experts will help you figure out the best fit!",
        },
      ],
    },
  },
  auth: {
    heroTitle: "Your Insurance, Simplified",
    heroDescription:
      "Discover, compare, and buy insurance plans from leading insurers with a simple and seamless digital experience.",
    features: [
      {
        title: "Insurance Across Categories",
        description:
          "Health, Motor, Travel, Home, and more — find the coverage you need in one place",
        dotClassName: "bg-theme-success-strong",
      },
      {
        title: "Compare Plans Easily",
        description: "Review coverage, benefits, and premiums to make informed insurance decisions",
        dotClassName: "bg-theme-success-strong",
      },
    ],
    copyrightText: "© 2026 Nexsys Insurance Portal. All rights reserved.",
    brandSubtitle: "NexInsure",
    welcomeTitle: "Welcome Back",
    welcomeDescription: "Enter your credentials to access your dashboard",
    submitLabel: "Sign in",
    rememberLabel: "Remember me for 30 days",
    helpText: "Need access?",
    helpCtaLabel: "Contact your administrator",
    legalPrefix: "By signing in, you agree to our",
    legalLinks: [
      { label: "Terms of Service", href: "#" },
      { label: "Privacy Policy", href: "#" },
    ],
    otpFooterPrefix: "New to NexInsure?",
    otpFooterCta: "Get a quote",
    otpFooterSuffix: "to get started.",
  },
  authOc: {
    heroTitle: "Powering Your Insurer’s Retail & Broker Journeys",
    heroDescription:
      "A unified Omni Channel platform built exclusively for your insurer to connect, manage, and streamline Retail and Broker insurance journeys from quote to policy issuance.",
    features: [
      {
        title: "Retail & Broker Journeys",
        description:
          "Seamlessly manage customer and broker journeys through one connected platform.",
        dotClassName: "bg-theme-success-strong",
      },
      {
        title: "End-to-End Insurance Journey",
        description:
          "Track and manage the complete journey from quote creation to policy issuance.",
        dotClassName: "bg-theme-success-strong",
      },
      {
        title: "Insurer-Centric Platform",
        description:
          "Built exclusively for your insurer to deliver a consistent and efficient insurance experience.",
        dotClassName: "bg-theme-success-strong",
      },
    ],
  },
  journeys: {
    marketingConsentLabel:
      "I agree to receive updates from NexInsure about quotes, products and promotional offers through marketing channels including SMS and Email.",
    quoteMascotAlt: "Meet Layla",
    policyPending: {
      paymentPlanHeading: "EASY PAYMENT PLAN ON NexInsure",
      paymentPlanSubtext: "Credit Card (3, 6, 9 & 12 months)",
      specialistFollowUp: "One of our NexInsure specialists will contact you shortly.",
    },
    travel: {
      howItWorksHeading: "It’s easy to NexInsure",
      whyBetterHeading: "See what makes NexInsure better",
      paymentPlanText: "on NexInsure Credit Card",
      trustedByManyText: "Thousands insured with NexInsure",
    },
  },
  footer: {
    copyrightOwner: "Nexsys",
    copyrightLink: "https://nexsysit.co.in/",
    regulatoryText:
      "NexInsure empowers insurance brokers, aggregators, and banks to launch their own digital insurance marketplace in weeks. Compare quotes from multiple insurers, customize coverages, and bind policies seamlessly from a single platform.",
  },
} as const;
